/* eslint-env browser */
// src/auth0Provider.jsx
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { Auth0Provider } from "@auth0/auth0-react";
import { AdminAuthProvider, AuthProvider, GuestAuthProvider } from "./AuthContext";
import {
  ADMIN_LOGIN_ENABLED,
  AUTH0_AUDIENCE,
  AUTH_ENABLED,
  DEV_LOGIN_ENABLED,
  STUDENT_GATE_ENABLED,
} from "./config";

const Auth0ProviderWithHistory = ({ children }) => {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const location = useLocation();
  const navigate = useNavigate();

  const hasStoredAdminSession = (() => {
    if (typeof window === "undefined" || !window.localStorage) return false;
    try {
      return Boolean(window.localStorage.getItem("adminUser"));
    } catch {
      return false;
    }
  })();

  const hasStoredStudentSession = (() => {
    if (typeof window === "undefined" || !window.localStorage) return false;
    try {
      return Boolean(window.localStorage.getItem("studentGateUser"));
    } catch {
      return false;
    }
  })();

  const onRedirectCallback = (appState) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  // Check if the user agent is iOS Safari
  const isIosSafari = /iP(ad|hone|od).+Version\/[\d.]+.*Safari/i.test(
    navigator.userAgent,
  );

  const hasAuth0Config = Boolean(domain && clientId);
  const onAdminRoute = location.pathname.startsWith("/admin");
  const onStudentRoute = location.pathname.startsWith("/elev");
  const shouldUseAdminProvider =
    ADMIN_LOGIN_ENABLED &&
    (hasStoredAdminSession || (onAdminRoute && (!AUTH_ENABLED || !hasAuth0Config)));
  const shouldUseStudentProvider =
    (onStudentRoute || hasStoredStudentSession) && STUDENT_GATE_ENABLED;

  if (shouldUseAdminProvider) {
    return <AdminAuthProvider>{children}</AdminAuthProvider>;
  }
  if (shouldUseStudentProvider) {
    return <AuthProvider>{children}</AuthProvider>;
  }
  if (!AUTH_ENABLED || !hasAuth0Config) {
    if (DEV_LOGIN_ENABLED) {
      return <AuthProvider>{children}</AuthProvider>;
    }
    return <GuestAuthProvider>{children}</GuestAuthProvider>;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        ...(AUTH0_AUDIENCE ? { audience: AUTH0_AUDIENCE } : {}),
        scope: "openid profile email offline_access",
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      useCookiesForTransactions={isIosSafari}
    >
      <AuthProvider>{children}</AuthProvider>
    </Auth0Provider>
  );
};

Auth0ProviderWithHistory.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Auth0ProviderWithHistory;
